import React, { useState, useCallback, useMemo, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { flatten, isEmpty, isArray, noop } from 'underscore';
import classnames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import {
  useSiteConfig,
  useInstrumentationServiceContext,
  useIsSmartPageM365StockImageGalleryEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  useUploadService,
  useFacebookPhotos,
  SmartPivotImageEditor,
  AddImagesPlaceholder,
  EditImagesView,
  ImageOptionsKey,
} from '@bingads-webui-campaign-react/smart-component-image';
import {
  getISOTimeStringWithTimeZone,
  getUploadUrlFromImage,
  ImageSource,
  getUploadDetailFromImage,
  DATA_SOURCE_KEY_MAPPING,
  getDataSourceByKey,
  truncateStringEndWithEmoji,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  SmartTextEditor,
} from '@bingads-webui-campaign-react/smart-component-text';

import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Label } from '@fluentui/react/lib/Label';

import { CARD_DIALOG_MODE } from './constants';

const getStyles = () => ({
  cardEditor: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: 24,
  },
  cardEditorSidePanel: {
    width: 448,
  },
  headerStyle: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '27px',
    color: '#323130',
    margin: '23px 0px',
    border: '0px',
    padding: '0px',
  },
  cardAuthorTextArea: {
    width: 400,
    height: 70,
  },
  cardTextArea: {
    marginBottom: 60,
    width: 400,
    minHeight: 200,
  },
  cardImageArea: {
    marginBottom: 136,
  },
  smartTextEditor: {
    '& .ms-TextField-messageError': {
      justifyContent: 'flex-end',
    },
  },
  warningIcon: {
    fontSize: '16px',
    lineHeight: '19px',
    marginRight: '6px',
    color: '#0078D4',
  },
  cardEditorPreview: {
    width: 504,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    background: '#E8E8E8',
    borderLeft: '1px solid lightgray',
    '& label': {
      position: 'absolute',
      top: 24,
      left: 20,
    },
    '& .card': {
      background: '#fff',
    },
    '& > div': {
      marginBottom: 0,
    },
  },
  dialogContentStyle: {
    '& .ms-Dialog-header': {
      '& .ms-Dialog-button--close': {
        zIndex: 1,
      },
    },
    '& .ms-Dialog-title': {
      minHeight: 0,
      padding: 0,
    },
    '& .ms-Dialog-inner': {
      padding: 0,
    },
    '& .ms-Dialog-actions': {
      marginTop: 0,
      paddingTop: 16,
      borderTop: '1px solid lightgray',
    },
    '& .ms-Dialog-actionsRight': {
      marginRight: 32,
      paddingBottom: 16,
      display: 'flex',
      justifyContent: 'end',
      '& .ms-Dialog-action': {
        display: 'flex',
        alignItems: 'center',
      },
    },
  },
  imageError: {
    color: '#A80000',
    fontSize: 14,
  },
});

const UnStyledCardDialog = ({
  ItemComponent,
  generateItemProperty,
  mapItemToCard,
  targetItem,
  authorCharacterLimit,
  characterSoftLimit,
  characterHardLimit,
  labelTexts,
  i18n,
  classes,
  onAdd,
  onCancel,
  gotoSettings,
  cardType,
  supportImageInput,
  supportAuthorInput,
}) => {
  const { siteConfig } = useSiteConfig();
  const logger = useInstrumentationServiceContext();
  const smartPageId = siteConfig.id;

  const businessCategories =
    getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.BusinessCategories);

  let currentCard = null;
  let isCreate = true;

  if (targetItem) {
    currentCard = mapItemToCard(targetItem);
    isCreate = false;
  }

  const [isLoading, setIsLoading] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState(ImageSource.StockImage);
  const [cardAuthor, setCardAuthor] = useState(currentCard && currentCard.author ? currentCard.author : '');
  const [cardMessage, setCardMessage] = useState(currentCard && currentCard.message ? currentCard.message : '');
  const [cardPhotoUrl, setCardPhotoUrl] = useState(currentCard ? currentCard.photo : undefined);
  const [imageError, setImageError] = useState('');
  // Need this ref, otherwise will make useFacebookPhotos rerender infinitely.
  const emptyImageListRef = useRef([]);

  const { uploadImageAsync, bulkUploadImagesByUrlsAsync } = useUploadService();

  const isSmartPageM365StockImageGalleryEnabled = useIsSmartPageM365StockImageGalleryEnabled();
  const [shouldStartFetchFbPhotos, setShouldStartFetchFbPhotos]
    = useState(!isSmartPageM365StockImageGalleryEnabled);
  const {
    isFbSupported,
    fbPhotos,
    isInitializing,
  } = useFacebookPhotos(emptyImageListRef.current, shouldStartFetchFbPhotos);

  const onAddClicked = () => {
    const date = new Date();
    const time = getISOTimeStringWithTimeZone(date);
    const cardId = `${smartPageId}_${date.getTime()}`;

    let newItem = generateItemProperty({
      current: targetItem,
      time,
      cardId,
      cardAuthor,
      cardMessage,
      cardPhotoUrl,
    });

    if ((isCreate && !cardPhotoUrl) ||
      (currentCard && currentCard.photo === cardPhotoUrl)) {
      if (isCreate) {
        logger.logTrace(`Create ${cardType} with text : ${JSON.stringify(newItem)}`);
      } else {
        logger.logTrace(`Update ${cardType} ${JSON.stringify(targetItem)} with text : ${JSON.stringify(newItem)}`);
      }

      onAdd({ newSelections: [newItem] });

      return;
    }

    const imageIdsToRemove = [];
    if (currentCard && currentCard.photo && !cardPhotoUrl) {
      logger.logTrace(`Remove image of ${cardType} ${JSON.stringify(targetItem)}: ${JSON.stringify(newItem)}`);
      imageIdsToRemove.push(currentCard.imageId);
      onAdd({ newSelections: [newItem], imageIdsToRemove });

      return;
    }

    const uploadImagePromise = selectedImageType === ImageSource.Local ?
      uploadImageAsync(cardPhotoUrl) : bulkUploadImagesByUrlsAsync([cardPhotoUrl]);

    uploadImagePromise
      .finally((() => {
        setIsLoading(true);
        return () => {
          setIsLoading(false);
        };
      })())
      .then((result) => {
        const imageToAdd = isArray(result) ? result[0] : result;

        if (currentCard && currentCard.imageId) {
          imageIdsToRemove.push(currentCard.imageId);
        }
        const { IdString, ImageUrl } = imageToAdd;

        newItem = generateItemProperty({
          current: targetItem,
          time,
          cardId,
          cardAuthor,
          cardMessage,
          cardPhotoUrl: ImageUrl,
          cardImageId: IdString,
          cardExternalSrc: selectedImageType === ImageSource.StockImage ? cardPhotoUrl : undefined,
        });

        if (isCreate) {
          logger.logTrace(`Create ${cardType} with image : ${JSON.stringify(newItem)}`);
        } else {
          logger.logTrace(`Update ${cardType} ${JSON.stringify(targetItem)} with image : ${JSON.stringify(newItem)}, remove imageId: ${flatten(imageIdsToRemove).toString()}`);
        }

        onAdd({
          newSelections: [newItem],
          imagesToAdd: [imageToAdd],
          imageIdsToRemove,
        });
      })
      .catch((err) => {
        logger.logTrace(`SmartPage|CreateDialog|AddImageError, ${JSON.stringify(err)}`);
        setImageError(i18n.getString(_TL_('Failed to add the image. Please try again')));
      });
  };

  const dialogContentProps = useMemo(() => ({
    className: classes.dialogContentStyle,
    type: DialogType.close,
  }), [classes]);

  const generateCardMessageError = useCallback((currentLength) => {
    const text = i18n.getString(labelTexts.maximumCharacterErrorText, {
      length: currentLength, softLimit: characterSoftLimit,
    });

    return (
      <div>
        <span className={classnames(classes.warningIcon, 'iconba', 'iconba-IncidentTriangle')} />
        {text}
      </div>
    );
  }, [characterSoftLimit, classes.warningIcon, i18n, labelTexts.maximumCharacterErrorText]);

  const [messageError, setMessageError]
    = useState(cardMessage.length > characterSoftLimit ? generateCardMessageError(cardMessage.length) : '');

  const onTextAreaChange = useCallback(({
    limit,
    setText,
    setError,
    getErrorMsg,
  }) => ({ value: newValue }) => {
    if (newValue.length > limit) {
      setError(getErrorMsg(newValue.length));
    } else {
      setError('');
    }

    setText(newValue.trim() ? newValue : '');
  }, []);

  const isAddBtnDisabled = useCallback(
    () => {
      if ((supportImageInput && imageError)) {
        return true;
      }

      const hasTextInput = !isEmpty(cardMessage) && (!supportAuthorInput || !isEmpty(cardAuthor));
      const hasImageInput = !isEmpty(cardPhotoUrl);
      if (!hasTextInput && !hasImageInput) {
        return true;
      }

      return !isCreate &&
        currentCard.message === cardMessage.trim() &&
        currentCard.photo === cardPhotoUrl &&
        currentCard.author === cardAuthor.trim();
    },
    [
      isCreate,
      supportImageInput,
      supportAuthorInput,
      imageError,
      currentCard,
      cardMessage,
      cardPhotoUrl,
      cardAuthor,
    ]
  );

  const onImageSelectionChanged = useCallback((selectionList) => {
    if (isArray(selectionList) && !isEmpty(selectionList)) {
      const selection = selectionList[0];
      const url = getUploadUrlFromImage(selection) || selection.imageData;

      const { source } = getUploadDetailFromImage(selection);
      setSelectedImageType(source);

      setCardPhotoUrl(url);
      setImageError('');
    }
  }, []);

  const onPivotLinkClicked = useCallback((key) => {
    if (key === ImageOptionsKey.FACEBOOKIMPORT) {
      setShouldStartFetchFbPhotos(true);
    }
  }, []);

  const serializeTextInputValue = useCallback(val =>
    truncateStringEndWithEmoji(val, characterHardLimit), [characterHardLimit]);
  const serializeAuthorInputValue = useCallback(val =>
    truncateStringEndWithEmoji(val, authorCharacterLimit), [authorCharacterLimit]);

  const onDeleteImage = useCallback(() => {
    setCardPhotoUrl(undefined);
    setImageError('');
  }, []);

  return (
    <Dialog
      hidden={false}
      onDismiss={onCancel}
      dialogContentProps={dialogContentProps}
      minWidth={1000}
      modalProps={{ isBlocking: true }}
    >
      <UiBlocker
        blocking={isLoading}
        loadingText={isCreate ? i18n.getString(_TL_('Adding')) : i18n.getString(_TL_('Updating'))}
      >
        <div className={classes.cardEditor}>
          <div className={classes.cardEditorSidePanel}>
            {supportImageInput && showImageEditor ?
              <div id="customized-card-image-editor-container">
                <DefaultButton
                  id="back-to-editor-button"
                  className={classes.headerStyle}
                  onClick={() => setShowImageEditor(false)}
                  iconProps={{ iconName: 'Back' }}
                  text={i18n.getString(_TL_('Back'))}
                />
                <SmartPivotImageEditor
                  className={classnames('customized-card-image-editor')}
                  i18n={i18n}
                  onSelectionChanged={onImageSelectionChanged}
                  gotoSettings={gotoSettings}
                  isLoadingFbPhotos={isInitializing}
                  isFbSupported={isFbSupported}
                  isMultiple={false}
                  itemsPerPage={4}
                  defaultKeywords={businessCategories}
                  fbPhotos={fbPhotos}
                  onPivotLinkClicked={onPivotLinkClicked}
                />
              </div> :
              <Fragment>
                <h2 className={classnames('card-dialog-title', classes.headerStyle)}>
                  {isCreate ? labelTexts.creationTitleText : labelTexts.editTitleText}
                </h2>
                {
                  supportAuthorInput &&
                  <div className={classes.cardAuthorTextArea}>
                    <SmartTextEditor
                      id="customized-card-author-input"
                      className={classes.smartTextEditor}
                      label={labelTexts.authorSubtitleText}
                      value={cardAuthor}
                      onChange={onTextAreaChange({
                        limit: authorCharacterLimit,
                        setText: setCardAuthor,
                        setError: noop,
                        getErrorMsg: noop,
                      })}
                      resizable={false}
                      rows={1}
                      maxCharCount={authorCharacterLimit}
                      serializeValue={serializeAuthorInputValue}
                    />
                  </div>
                }
                <div className={classes.cardTextArea}>
                  <SmartTextEditor
                    id="customized-card-message-input"
                    className={classes.smartTextEditor}
                    label={labelTexts.messageSubtitleText}
                    value={cardMessage}
                    onChange={onTextAreaChange({
                      limit: characterSoftLimit,
                      setText: setCardMessage,
                      setError: setMessageError,
                      getErrorMsg: generateCardMessageError,
                    })}
                    errorMessage={messageError}
                    multiline
                    resizable={false}
                    rows={8}
                    maxCharCount={characterSoftLimit}
                    serializeValue={serializeTextInputValue}
                    invalid={false}
                  />
                </div>
                {
                  supportImageInput &&
                  <div id="customized-card-selected-image" className={classes.cardImageArea}>
                    <Label>{labelTexts.imageSubtitleText}</Label>
                    {
                      cardPhotoUrl ?
                        <EditImagesView
                          items={[{ externalSrc: cardPhotoUrl }]}
                          i18n={i18n}
                          onDelete={onDeleteImage}
                          onReplace={() => setShowImageEditor(true)}
                          deleteOnly={false}
                        /> :
                        <AddImagesPlaceholder
                          i18n={i18n}
                          onClick={() => setShowImageEditor(true)}
                          text={i18n.getString(_TL_('Add photo'))}
                        />
                    }
                  </div>
                }
              </Fragment>
            }
          </div>
          <div id="customized-card-preview" className={classes.cardEditorPreview}>
            <ItemComponent
              i18n={i18n}
              mode={CARD_DIALOG_MODE.VIEW_MODE}
              item={generateItemProperty({
                time: currentCard && currentCard.time ?
                  currentCard.time :
                  getISOTimeStringWithTimeZone(new Date()),
                cardAuthor,
                cardMessage,
                cardPhotoUrl,
              })}
            />
          </div>
        </div>

        <DialogFooter>
          {imageError &&
            <div role="alert" className={classnames(classes.imageError, 'card-image-error-message')}>
              {imageError}
            </div>
          }
          <PrimaryButton
            disabled={isAddBtnDisabled()}
            onClick={onAddClicked}
            text={isCreate ? (labelTexts.primaryButtonText || i18n.getString(_TL_('Add'))) : i18n.getString(_TL_('Update'))}
          />
          <DefaultButton onClick={onCancel} text={i18n.getString(_TL_('Cancel'))} />
        </DialogFooter>
      </UiBlocker>
    </Dialog>
  );
};

UnStyledCardDialog.propTypes = {
  ItemComponent: PropTypes.func.isRequired,
  generateItemProperty: PropTypes.func.isRequired,
  mapItemToCard: PropTypes.func.isRequired,
  authorCharacterLimit: PropTypes.number,
  characterSoftLimit: PropTypes.number.isRequired,
  characterHardLimit: PropTypes.number.isRequired,
  labelTexts: PropTypes.shape({
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
  }).isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  targetItem: PropTypes.shape({
  }),
  gotoSettings: PropTypes.func.isRequired,
  cardType: PropTypes.string.isRequired,
  supportImageInput: PropTypes.bool,
  supportAuthorInput: PropTypes.bool,
};

UnStyledCardDialog.defaultProps = {
  authorCharacterLimit: 30,
  targetItem: undefined,
  supportImageInput: false,
  supportAuthorInput: false,
};

export const CardDialog = withDefaultStyles(
  UnStyledCardDialog,
  getStyles
);
